import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import { globalConfig } from '~src/context/config';
import { useGlobalState } from '~src/global-state';

type SubregionResponse = {
  locale: string;
  translations: Record<string, string>;
};

export function countrySubregionsLocalizationGet(locale: string, purchaseCountry: string) {
  return axios.get<Required<SubregionResponse>>(
    `${globalConfig.cdnUrl}/country-regions-locales/${locale}/l2/${purchaseCountry}.json`,
    {
      transformResponse: [
        // imitate LocaleResponse
        data => ({ locale, translations: JSON.parse(data) }),
      ],
    }
  );
}

export function useCountrySubregionsLocalizationQuery() {
  const globalState = useGlobalState();

  return useQuery<Required<SubregionResponse>, AxiosError>({
    queryKey: ['country-subregions', globalState.purchase_country, globalState.locale],
    queryFn: async () => {
      const { data } = await countrySubregionsLocalizationGet(
        globalState.locale,
        globalState.purchase_country
      );
      return data;
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

export function useSubregions() {
  const globalState = useGlobalState();
  const queryClient = useQueryClient();
  const subregions = queryClient.getQueryData<SubregionResponse>([
    'country-subregions',
    globalState.purchase_country,
    globalState.locale,
  ]);

  if (!subregions?.translations) {
    return [];
  }

  return Object.entries(subregions.translations).map(([code, region]) => ({
    value: code,
    label: region,
  }));
}
